import PropTypes from 'prop-types';
import { numberFormat, unitToString } from '@/utils/unitUtils';

const formatQuantity = (quantity, digits = 2) => {
  if (!quantity || quantity.scalar == null) return '—';
  return `${numberFormat(quantity.scalar, digits)} ${unitToString(quantity.unit) || ''}`.trim();
};

const FeedstockUtilityCosts = ({ breakdown }) => {
  if (!breakdown || !breakdown.rows || breakdown.rows.length === 0) {
    return null;
  }

  return (
    <section className="py-4 border-b border-gray-200 flex flex-col gap-3">
      <div>
        <h3 className="text-sm font-semibold text-gray-900">Feedstock &amp; utility costs</h3>
        <p className="text-xs text-gray-500">
          Each annual cost is a unit price × the amount consumed. For a feedstock, the unit price is the cost of that
          stream as delivered to this module — for streams produced upstream it already includes everything spent to
          make and transport them, so it grows as you move down the supply chain. For a utility (e.g. electricity,
          heat), it&apos;s simply the price paid per unit.
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-gray-200 text-[11px] uppercase tracking-wide text-gray-500">
              <th className="py-2 pr-3 text-left font-normal">Item</th>
              <th className="py-2 px-3 text-left font-normal">Unit price</th>
              <th className="py-2 px-3 text-left font-normal">Consumption</th>
              <th className="py-2 pl-3 text-left font-normal">Annual cost</th>
            </tr>
          </thead>
          <tbody>
            {breakdown.rows.map(row => (
              <tr key={`${row.kind}-${row.label}`} className="border-b border-gray-50">
                <td className="py-2 pr-3 text-sm text-gray-700 whitespace-nowrap">
                  {row.label}
                  <span className="ml-2 text-[11px] uppercase tracking-wide text-gray-400">{row.kind}</span>
                </td>
                <td className="px-3 py-2 text-sm tabular-nums text-gray-800 whitespace-nowrap">
                  {formatQuantity(row.unit_price, 4)}
                </td>
                <td className="px-3 py-2 text-sm tabular-nums text-gray-500 whitespace-nowrap">
                  <span className="text-gray-400 mr-1">×</span>
                  {formatQuantity(row.consumption, 2)}
                </td>
                <td className="pl-3 py-2 text-sm tabular-nums font-semibold text-gray-900 whitespace-nowrap">
                  <span className="text-gray-400 mr-1">=</span>
                  {formatQuantity(row.total, 2)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};

FeedstockUtilityCosts.propTypes = {
  breakdown: PropTypes.shape({
    rows: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        kind: PropTypes.string,
        unit_price: PropTypes.object,
        consumption: PropTypes.object,
        total: PropTypes.object,
      }),
    ),
  }),
};

export default FeedstockUtilityCosts;
